/**
 * Side-panel state for the per-row ``ExceptionDrawer``.
 *
 * Tracks the row whose drawer is currently open, derives a stable
 * string key for the active-row visual cue, and scrolls the active row
 * into view when the drawer opens. Pulled out of ``DataViewTable``
 * so the host file only carries render orchestration.
 */
import { useEffect, useMemo, useState } from 'react'

export interface ExceptionDrawerState {
  drawerRow: Record<string, any> | null
  setDrawerRow: (row: Record<string, any> | null) => void
  activeDrawerRowKey: string | null
}

export function useExceptionDrawer(
  rowKeyField: string,
): ExceptionDrawerState {
  const [drawerRow, setDrawerRow] = useState<Record<string, any> | null>(null)

  // Stable row key of the row whose drawer is open. Drives the
  // active-row tint in TableBodyRows. Computed from the same row-key
  // field the table already uses to track expand/selection, so the
  // tint lands on the same DOM row even when rowKeyField is a
  // synthetic composite (`row_key`, `ls_id`, etc.).
  const activeDrawerRowKey = useMemo(() => {
    if (!drawerRow) return null
    const key = drawerRow[rowKeyField]
    return key !== undefined && key !== null ? String(key) : null
  }, [drawerRow, rowKeyField])

  // Bring the source row into view when the drawer opens. Scoped by
  // `data-row-key` so we hit the exact row regardless of pagination,
  // filters, or rowSplit duplication. `block: 'nearest'` avoids
  // re-centring rows that are already visible; `behavior: 'smooth'`
  // keeps the motion subtle. Effect keys on activeDrawerRowKey so it
  // only fires on row change, not on every render.
  useEffect(() => {
    if (!activeDrawerRowKey) return
    const el = document.querySelector(
      `[data-row-key="${CSS.escape(activeDrawerRowKey)}"]`,
    )
    if (el) {
      (el as HTMLElement).scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [activeDrawerRowKey])

  return { drawerRow, setDrawerRow, activeDrawerRowKey }
}
